import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { getDailyAnalytics, getTopUsersDaily } from "../../Service/apiService";
import "../../assets/css/DailyDataAnalytics.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaUser } from "react-icons/fa";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

// Robust time scale config — prevents disappearing x-axis
const getTimeScaleConfig = (rows, range, customStart, customEnd) => {
  if (!rows?.length) {
    const now = new Date();
    return {
      unit: "day",
      min: new Date(now - 7 * 86400000),
      max: now,
    };
  }

  // Use actual data range as base
  const dates = rows.map((r) => new Date(r.date));
  let minDate = Math.min(...dates);
  let maxDate = Math.max(...dates);

  // Expand to filter range if wider
  const now = new Date();
  if (range === "custom") {
    if (customStart) minDate = Math.min(minDate, new Date(customStart));
    if (customEnd) maxDate = Math.max(maxDate, new Date(customEnd));
  } else {
    switch (range) {
      case "1w":
        minDate = now - 7 * 86400000;
        break;
      case "1m":
        minDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      case "3m":
        minDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        break;
      case "6m":
        minDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case "1y":
        minDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        break;
    }
    maxDate = now;
  }

  const diffDays = (maxDate - minDate) / 86400000;
  let unit;
  if (diffDays <= 14) unit = "day";
  else if (diffDays <= 90) unit = "week";
  else unit = "month";

  return {
    unit,
    min: new Date(minDate),
    max: new Date(maxDate),
  };
};

const getChartOptions = (scaleConfig) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: (ctx) => ctx[0]?.label || "",
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
      },
    },
  },
  scales: {
    x: {
      type: "time",
      min: scaleConfig.min,
      max: scaleConfig.max,
      time: {
        unit: scaleConfig.unit,
        tooltipFormat: "dd MMM yyyy",
        displayFormats: {
          day: "dd MMM",
          week: "dd MMM",
          month: "MMM yyyy",
        },
      },
      grid: { display: false },
      ticks: {
        font: { size: 11 },
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: "#eef2f7" },
      ticks: {
        font: { size: 11 },
        precision: 0,
      },
    },
  },
});
const ChartSkeleton = () => (
  <div style={{ padding: "16px" }}>
    <Skeleton height={18} width={140} />
    <Skeleton height={260} style={{ marginTop: 16 }} borderRadius={12} />
  </div>
);

const DailyDataAnalytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [loginTimeRange, setLoginTimeRange] = useState("1w");
  const [loginCustomStart, setLoginCustomStart] = useState("");
  const [loginCustomEnd, setLoginCustomEnd] = useState("");

  const [searchTimeRange, setSearchTimeRange] = useState("1w");
  const [searchCustomStart, setSearchCustomStart] = useState("");
  const [searchCustomEnd, setSearchCustomEnd] = useState("");

  const [failedLoginTimeRange, setFailedLoginTimeRange] = useState("1w");
  const [failedLoginCustomStart, setFailedLoginCustomStart] = useState("");
  const [failedLoginCustomEnd, setFailedLoginCustomEnd] = useState("");

  //pop-up top 10 users daily
  const [topUsersModalOpen, setTopUsersModalOpen] = useState(false);
  const [modalTimeRange, setModalTimeRange] = useState("today");
  const [modalCustomDate, setModalCustomDate] = useState("");
  const [topUsersData, setTopUsersData] = useState([]);
  const [topUsersLoading, setTopUsersLoading] = useState(false);

  //for start and end date set today
  const [modalCustomStart, setModalCustomStart] = useState("");
  const [modalCustomEnd, setModalCustomEnd] = useState("");

  // Set today as default end date
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const sevenDaysAgo = new Date(Date.now() - 6 * 86400000)
      .toISOString()
      .split("T")[0];

    setLoginCustomEnd(today);
    setSearchCustomEnd(today);
    setFailedLoginCustomEnd(today);
    setModalCustomEnd(today);
    setModalCustomStart(sevenDaysAgo);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getDailyAnalytics();
        const sorted = (res?.data || []).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        setData(sorted);
      } catch (err) {
        console.error("Failed to load analytics:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Date range helper
  const getRange = (range, start, end) => {
    const now = new Date();
    if (range === "custom") {
      return {
        start: start ? new Date(start) : new Date(now.getFullYear() - 1, 0, 1),
        end: end ? new Date(end) : now,
      };
    }

    switch (range) {
      case "1w":
        return { start: new Date(now - 7 * 86400000), end: now };
      case "1m":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          end: now,
        };
      case "3m":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 3, 1),
          end: now,
        };
      case "6m":
        return {
          start: new Date(now.getFullYear(), now.getMonth() - 6, 1),
          end: now,
        };
      case "1y":
      default:
        return {
          start: new Date(now.getFullYear() - 1, now.getMonth(), 1),
          end: now,
        };
    }
  };

  // Filter data
  const filterData = (range, start, end, raw) => {
    if (!raw.length) return [];
    const { start: s, end: e } = getRange(range, start, end);
    return raw.filter((d) => {
      const dt = new Date(d.date);
      return dt >= s && dt <= e;
    });
  };

  // Filtered datasets
  const filteredLogins = useMemo(
    () => filterData(loginTimeRange, loginCustomStart, loginCustomEnd, data),
    [data, loginTimeRange, loginCustomStart, loginCustomEnd]
  );

  const filteredSearches = useMemo(
    () => filterData(searchTimeRange, searchCustomStart, searchCustomEnd, data),
    [data, searchTimeRange, searchCustomStart, searchCustomEnd]
  );

  const filteredFailedLogins = useMemo(
    () =>
      filterData(
        failedLoginTimeRange,
        failedLoginCustomStart,
        failedLoginCustomEnd,
        data
      ),
    [data, failedLoginTimeRange, failedLoginCustomStart, failedLoginCustomEnd]
  );

  // Scale configs (fixes disappearing x-axis)
  const loginScale = getTimeScaleConfig(
    filteredLogins,
    loginTimeRange,
    loginCustomStart,
    loginCustomEnd
  );
  const searchScale = getTimeScaleConfig(
    filteredSearches,
    searchTimeRange,
    searchCustomStart,
    searchCustomEnd
  );
  const failedScale = getTimeScaleConfig(
    filteredFailedLogins,
    failedLoginTimeRange,
    failedLoginCustomStart,
    failedLoginCustomEnd
  );

  // Chart data helper
  const makeLineData = (rows, key, label, color) => ({
    labels: rows.map((d) => new Date(d.date)),
    datasets: [
      {
        label,
        data: rows.map((d) => d[key] || 0),
        borderColor: color,
        backgroundColor: `${color}22`,
        tension: 0.4,
        fill: true,
        borderWidth: 2.5,
      },
    ],
  });

  const timeOptions = [
    { label: "7 Days", value: "1w" },
    { label: "1 Month", value: "1m" },
    { label: "3 Months", value: "3m" },
    { label: "6 Months", value: "6m" },
    { label: "1 Year", value: "1y" },
    { label: "Custom Range", value: "custom" },
  ];

  // Loading & empty states

  // Safe min/max for Active Users
  const activeUserValues = filteredLogins.map((d) => d.activeUsers || 0);
  const minActive = activeUserValues.length
    ? Math.max(0, Math.min(...activeUserValues) - 5)
    : 0;
  const maxActive = activeUserValues.length
    ? Math.max(...activeUserValues) + 5
    : 10;

  const getModalDate = useCallback(() => {
    const today = new Date();
    let dateStr;

    switch (modalTimeRange) {
      case "today":
        dateStr = today.toISOString().split("T")[0];
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        dateStr = yesterday.toISOString().split("T")[0];
        break;
      case "7d":
        // Still show today — you can change to latest day with data later
        dateStr = today.toISOString().split("T")[0];
        break;
      case "custom":
        dateStr = modalCustomDate || today.toISOString().split("T")[0];
        break;
      default:
        dateStr = today.toISOString().split("T")[0];
    }
    return dateStr;
  }, [modalTimeRange, modalCustomDate]);

  // Add inside the component
  const fetchTopUsersForModal = async () => {
    setTopUsersData([]);
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Not authenticated");
      return;
    }

    setTopUsersLoading(true);
    try {
      let params;
      const today = new Date().toISOString().split("T")[0];

      switch (modalTimeRange) {
        case "today":
          params = { date: today };
          break;
        case "yesterday":
          const yesterday = new Date(Date.now() - 86400000)
            .toISOString()
            .split("T")[0];
          params = { date: yesterday };
          break;
        case "7d":
          const sevenDaysAgo = new Date(Date.now() - 6 * 86400000)
            .toISOString()
            .split("T")[0];
          params = { startDate: sevenDaysAgo, endDate: today };
          break;
        case "custom":
          params = {
            startDate:
              modalCustomStart ||
              new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0],
            endDate: modalCustomEnd || today,
          };
          break;
        default:
          params = { date: today };
      }

      const data = await getTopUsersDaily(params, token);
      setTopUsersData(data || []);
    } catch (err) {
      console.error("Failed to fetch top users:", err);
      setTopUsersData([]);
    } finally {
      setTopUsersLoading(false);
    }
  };

  useEffect(() => {
    if (topUsersModalOpen) {
      fetchTopUsersForModal();
    }
  }, [topUsersModalOpen, modalTimeRange, modalCustomStart, modalCustomEnd]);
  return (
    <div className="daily-analytics-dashboard">
      <div className="daily-analytics-grid">
        {/* DAILY LOGINS */}
        <AnalyticsCard
          title="Daily Logins"
          range={loginTimeRange}
          setRange={setLoginTimeRange}
          onStartChange={setLoginCustomStart}
          onEndChange={setLoginCustomEnd}
          startValue={loginCustomStart}
          endValue={loginCustomEnd}
          options={timeOptions}
          loading={loading} // ← pass loading state
        >
          <Line
            data={makeLineData(
              filteredLogins,
              "dailyLoginCount",
              "Logins",
              "#053763"
            )}
            options={getChartOptions(loginScale)}
          />
        </AnalyticsCard>

        {/* DAILY SEARCHES */}
        <AnalyticsCard
          title="Daily Searches"
          range={searchTimeRange}
          setRange={setSearchTimeRange}
          onStartChange={setSearchCustomStart}
          onEndChange={setSearchCustomEnd}
          startValue={searchCustomStart}
          endValue={searchCustomEnd}
          options={timeOptions}
          loading={loading}
        >
          <div style={{ position: "relative", height: "100%" }}>
            <Line
              data={makeLineData(
                filteredSearches,
                "dailySearchCount",
                "Searches",
                "#194a7a"
              )}
              options={getChartOptions(searchScale)}
            />
            {/*  Top Users Button */}
            {!loading && (
              <button
                className="top-users-icon-btn"
                onClick={() => {
                  const today = new Date().toISOString().split("T")[0];
                  setModalTimeRange("today");
                  setModalCustomStart(today);
                  setModalCustomEnd(today);
                  setTopUsersData([]);
                  setTopUsersModalOpen(true);
                }}
                title="View Top Users"
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "#053763",
                  color: "white",
                  border: "none",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
              >
                <FaUser size={14} />
              </button>
            )}
          </div>
        </AnalyticsCard>

        {/* FAILED LOGINS */}
        <AnalyticsCard
          title="Failed Logins"
          range={failedLoginTimeRange}
          setRange={setFailedLoginTimeRange}
          onStartChange={setFailedLoginCustomStart}
          onEndChange={setFailedLoginCustomEnd}
          startValue={failedLoginCustomStart}
          endValue={failedLoginCustomEnd}
          options={timeOptions}
          loading={loading}
        >
          <Line
            data={makeLineData(
              filteredFailedLogins,
              "dailyFailedLoginCount",
              "Failed Logins",
              "#194a7a"
            )}
            options={getChartOptions(failedScale)}
          />
        </AnalyticsCard>

        {/* ACTIVE USERS */}
        <AnalyticsCard
          title="Active Users"
          range={loginTimeRange}
          setRange={setLoginTimeRange}
          onStartChange={setLoginCustomStart}
          onEndChange={setLoginCustomEnd}
          startValue={loginCustomStart}
          endValue={loginCustomEnd}
          options={timeOptions}
          loading={loading}
        >
          <Line
            data={makeLineData(
              filteredLogins,
              "activeUsers",
              "Active Users",
              "#194a7a"
            )}
            options={{
              ...getChartOptions(loginScale),
              plugins: { legend: { display: true } },
              scales: {
                ...getChartOptions(loginScale).scales,
                y: {
                  beginAtZero: false,
                  suggestedMin: minActive,
                  suggestedMax: maxActive,
                },
              },
            }}
          />
        </AnalyticsCard>
      </div>
      {/* TOP USERS MODAL */}
      {topUsersModalOpen && (
        <div
          className="top-users-modal-overlay"
          onClick={() => setTopUsersModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="top-users-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              zIndex: 10000,
              padding: "20px",
              position: "relative",
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setTopUsersModalOpen(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#666",
              }}
            >
              &times;
            </button>

            <h3 style={{ marginBottom: "16px", textAlign: "center" }}>
              Top Users
            </h3>

            {/* Time Range Selector */}
            <div style={{ marginBottom: "16px" }}>
              <select
                value={modalTimeRange}
                onChange={(e) => setModalTimeRange(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  marginRight: "10px",
                }}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7d">Last 7 Days</option>
                <option value="custom">Custom Range</option>
              </select>

              {modalTimeRange === "custom" && (
                <div style={{ display: "inline-block", marginLeft: "10px" }}>
                  <input
                    type="date"
                    value={modalCustomStart}
                    onChange={(e) => setModalCustomStart(e.target.value)}
                    style={{
                      padding: "6px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      marginRight: "6px",
                    }}
                  />
                  <span style={{ margin: "0 4px" }}>to</span>
                  <input
                    type="date"
                    value={modalCustomEnd}
                    onChange={(e) => setModalCustomEnd(e.target.value)}
                    style={{
                      padding: "6px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
              )}
            </div>

            {/* Loading */}
            {topUsersLoading ? (
              <div style={{ padding: "20px" }}>
                <Skeleton count={10} height={36} />
              </div>
            ) : topUsersData.length === 0 ? (
              <p style={{ textAlign: "center", color: "#666" }}>
                No data available
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {topUsersData.length > 1 && (
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        Date
                      </th>
                    )}
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      S no
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Daily
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Total
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Last Search
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topUsersData.flatMap((day) =>
                    day.topUsers.map((user, index) => (
                      <tr
                        key={`${day.date}-${user.userId}`}
                        style={{ borderBottom: "1px solid #f5f5f5" }}
                      >
                        {/* Show date only if multiple days */}
                        {topUsersData.length > 1 && (
                          <td style={{ padding: "8px" }}>{day.date}</td>
                        )}
                        <td style={{ padding: "8px" }}>{index + 1}</td>
                        <td style={{ padding: "8px" }}>{user.name}</td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {user.dailySearchCount}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {user.totalSearchCount.toLocaleString()}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {user.lastSearchTime}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Fixed AnalyticsCard — now shows custom date pickers
const AnalyticsCard = ({
  title,
  range,
  setRange,
  onStartChange,
  onEndChange,
  startValue,
  endValue,
  options,
  children,
  loading = false,
}) => (
  <div className="daily-analytics-card">
    <div className="daily-analytics-card-header">
      <h3>{title}</h3>

      <div className="daily-analytics-time-filter-container">
        {loading ? (
          <Skeleton height={36} width={140} />
        ) : (
          <select
            className="daily-analytics-time-range-select refined"
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}

        {range === "custom" && !loading && (
          <div className="daily-analytics-custom-date-picker">
            <input
              type="date"
              value={startValue || ""}
              onChange={(e) => onStartChange(e.target.value)}
              className="daily-analytics-date-input"
            />
            <input
              type="date"
              value={endValue || ""}
              onChange={(e) => onEndChange(e.target.value)}
              className="daily-analytics-date-input"
            />
          </div>
        )}
      </div>
    </div>

    <div className="daily-analytics-chart-wrapper">
      {loading ? <ChartSkeleton /> : children}
    </div>
  </div>
);

export default DailyDataAnalytics;
